/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from '../lib/config.js';
import { t } from '../lib/i18n.js';

/*
 * Applies the configuration to the shell.
 *
 * The shell carries every piece of chrome the Map Viewer has; this removes what
 * the tenant has not switched on and fills in the text. Building the markup
 * from the configuration instead would put it in JavaScript, where a
 * designer cannot reach it — which is the reason the Map Viewer moved here at
 * all.
 *
 * Chrome that is off is *removed*, not hidden. An element hidden with CSS
 * is still in the tab order and still read aloud, so a search button on a
 * map with no search would be a control that announces itself and does
 * nothing.
 */

/** Which configuration answer switches each `data-feature` on. */
function features(config) {
    return {
        toc: !!config.map?.showToc,
        search: !!config.map?.showSearch,
        editorial: !!config.map?.showEditorial,
        consent: !!config.uiText?.cookie?.enabled,
        preview: !!config.preview?.enabled,
        header: !!config.header?.shown,
        zoom: (config.map?.zoomLevels || 1) > 1,
        // Count the languages, not the language families. A map offering
        // `de_CH` and `de_AT` has one to switch to; counting the families
        // finds one entry and hides the switcher that would have listed it.
        i18n:
            !!config.client?.hasI18n &&
            Object.values(config.i18n?.langs || {}).flat().length > 1,
    };
}

/** Remove the chrome this map does not have. */
function applyFeatures(root, config) {
    const enabled = features(config);
    root.querySelectorAll('[data-feature]').forEach((element) => {
        if (!enabled[element.dataset.feature]) element.remove();
    });
}

/** Fill in the Map Viewer's own interface strings. */
function applyInterfaceText(root) {
    root.querySelectorAll('[data-i18n]').forEach((element) => {
        element.textContent = t(element.dataset.i18n);
    });
    root.querySelectorAll('[data-i18n-value]').forEach((element) => {
        element.value = t(element.dataset.i18nValue);
    });
    root.querySelectorAll('[data-i18n-aria-label]').forEach((element) => {
        element.setAttribute('aria-label', t(element.dataset.i18nAriaLabel));
    });
    root.querySelectorAll('[data-share]').forEach((element) => {
        element.textContent = t('a11y.shareVia', {
            network: element.dataset.share,
        });
    });
}

/** Fill in the labels an editor sets per map. */
function applyMapLabels(root, config) {
    const labels = {
        ...(config.uiText?.labels || {}),
        ...(config.uiText?.search || {}),
        tableOfContents: config.uiText?.labels?.tableOfContents,
        searchTerm: config.uiText?.search?.termPlaceholder,
    };
    root.querySelectorAll('[data-label]').forEach((element) => {
        const value = labels[element.dataset.label];
        if (value) element.textContent = value;
    });

    const field = root.querySelector('.search-field');
    if (field && labels.searchTerm) {
        field.placeholder = labels.searchTerm;
    }

    const form = root.querySelector('.search-form');
    if (form) {
        // `search.js` reads these off the form, as it did when the template
        // wrote them there.
        form.dataset.filters = JSON.stringify(config.ui?.searchFilters || {});
        form.dataset.noResultsMsg = config.uiText?.search?.noResults || '';
        form.dataset.allResultsMsg = config.uiText?.search?.allResults || '';
    }

    const editorial = root.querySelector('.tabs');
    if (editorial && config.uiText?.labels?.editorsNote) {
        editorial.setAttribute('aria-label', config.uiText.labels.editorsNote);
    }
}

/** Name the map region and apply the theme its own colours imply. */
function applyMapElement(root, config) {
    const map = root.querySelector('#map');
    if (!map) return;
    map.setAttribute('aria-roledescription', t('a11y.mapRoleDescription'));
    map.setAttribute('aria-label', config.seo?.heading || '');
    map.classList.toggle('bg--dark', !config.ui?.darkUiTheme);
    map.classList.toggle('backdrop--dark', !!config.ui?.darkBackdrop);
    map.classList.toggle('backdrop--light', !config.ui?.darkBackdrop);
}

/** Build one link per zoom level. */
function applyZoomRange(root, config) {
    const range = root.querySelector('.zoom-range');
    if (!range) return;
    const levels = config.map?.zoomLevels || 1;
    range.replaceChildren(
        ...Array.from({ length: levels }, (_unused, index) => {
            const level = index + 1;
            const link = document.createElement('a');
            link.href = `#m=${level}//`;
            link.className = `zoom-level-${level}`;
            link.setAttribute('aria-label', t('a11y.zoomLevel', { level }));
            link.textContent = String(level);
            return link;
        })
    );
}

/** Build the language switcher, listing every language but the current one. */
function applyLanguages(root, config) {
    const panel = root.querySelector('.language-panel');
    if (!panel) return;

    const current = config.i18n?.lang || '';
    const token = config.access?.token;
    const links = [];
    for (const [base, codes] of Object.entries(
        config.i18n?.langs || {}
    ).sort()) {
        for (const code of [...codes].sort()) {
            if (code === current) continue;
            const params = new URLSearchParams();
            if (token) params.set('access', token);
            params.set('lang', code);
            if (config.preview?.enabled) params.set('preview', '1');

            const link = document.createElement('a');
            link.href = `?${params}`;
            link.className = 'language-item';
            const tag = code.replace('_', '-').toLowerCase();
            link.lang = tag;
            link.hreflang = tag;
            link.textContent = base;
            if (code !== base) {
                const territory = document.createElement('span');
                territory.className = 'language-territory';
                territory.textContent = code.split('_').pop();
                link.appendChild(territory);
            }
            links.push(link);
        }
    }
    panel.replaceChildren(...links);

    const label = root.querySelector('.language-current');
    if (label) {
        const [base, territory] = current.split('_');
        label.textContent = base;
        if (territory) {
            const span = document.createElement('span');
            span.className = 'language-territory';
            span.textContent = territory;
            label.appendChild(span);
        }
    }
}

/**
 * Apply the configuration to the shell.
 *
 * @param {ParentNode} [root] - The document, or a fragment in a test.
 * @returns {ParentNode} The root it worked on.
 */
export function setupPage(root = document) {
    const config = getConfig();
    applyFeatures(root, config);
    applyInterfaceText(root);
    applyMapLabels(root, config);
    applyMapElement(root, config);
    applyZoomRange(root, config);
    applyLanguages(root, config);
    return root;
}

/** Apply the configuration to the page. */
export function init() {
    setupPage(document);
}
